import React from 'react';
import { Input } from '@/components/ui/input';
import { Video } from '@/types/civic-education';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface VideoFieldsProps {
  item: Video;
  onFieldChange: (field: keyof Video, value: any) => void;
}

const VideoFields = ({ item, onFieldChange }: VideoFieldsProps) => {
  const handleVideoSourceChange = (value: string) => {
    onFieldChange('source', value);
  };

  // Extract YouTube ID from full URL if pasted
  const handleVideoIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Check if it's a full YouTube URL and extract the ID
    if (value.includes('youtube.com') || value.includes('youtu.be')) {
      try {
        const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
        const match = value.match(regex);
        
        if (match && match[1]) {
          onFieldChange('video_id', match[1]);
          return;
        }
      } catch (error) {
        console.error("Error parsing YouTube URL:", error);
      }
    }
    
    // Otherwise just store the value as is
    onFieldChange('video_id', value);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="source" className="text-sm font-medium">Video Source</label>
        <Select 
          value={item.source || 'youtube'} 
          onValueChange={handleVideoSourceChange}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="youtube">YouTube</SelectItem>
            <SelectItem value="vimeo">Vimeo</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <label htmlFor="video_id" className="text-sm font-medium">Video ID</label>
        <Input
          id="video_id"
          value={item.video_id}
          onChange={handleVideoIdChange}
          required
        />
        <p className="text-xs text-muted-foreground">
          {item.source === 'youtube' || !item.source ? 
            "Enter the YouTube video ID (e.g., 'dQw4w9WgXcQ') or paste the full YouTube URL" : 
            "Enter the video ID for the selected platform"}
        </p>
      </div>
      
      <div className="space-y-2">
        <label htmlFor="duration" className="text-sm font-medium">Duration (minutes)</label>
        <Input
          id="duration"
          type="number"
          value={item.duration || ''}
          onChange={(e) => onFieldChange('duration', parseInt(e.target.value))}
        />
      </div>
      
      <div className="space-y-2">
        <label htmlFor="thumbnail_url" className="text-sm font-medium">Thumbnail URL</label>
        <Input
          id="thumbnail_url"
          value={item.thumbnail_url || ''}
          onChange={(e) => onFieldChange('thumbnail_url', e.target.value)}
        />
      </div>
    </div>
  );
};

export default VideoFields;
